import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

const inputVariants = cva(
  cn(
    'flex h-[56px] w-full text-sm transition-colors file:border-0 file:bg-transparent font-normal file:text-sm file:font-medium placeholder:text-[#9E9E9E] relative',
    'hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus-within:border-primary-foreground focus-within:ring-1 focus-within:ring-primary',
    'border-box items-center gap-2',
  ),
  {
    variants: {
      variant: {
        outlined: ['bg-transparent border border-border', 'ea-input__outlined'],
        filled: ['bg-[#F9FAFB]', 'ea-input__filled'],
      },

      rounded: {
        default: ['rounded-[8px]', 'ea-input__wrapper--rounded-default'],
        none: ['rounded-none', 'ea-input__wrapper--rounded-none'],
      },
    },

    defaultVariants: {
      rounded: 'default',
      variant: 'filled',
    },
  },
);
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'>,
    VariantProps<typeof inputVariants> {
  before?: React.ReactNode;
  after?: React.ReactNode;

  beforeOuter?: React.ReactNode;
  afterOuter?: React.ReactNode;

  className?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
  classNames?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, before, after, afterOuter, beforeOuter, classNames, ...props }, ref) => {
    return (
      <>
        {beforeOuter}
        <div
          className={cn(
            inputVariants({
              rounded: props.rounded,
              variant: props.variant,
            }),
            'ea-input__wrapper',
            'bg-[#F9FAFB]',
            props['aria-invalid'] &&
              'ea-input__wrapper--invalid border-destructive text-destructive',
            className?.wrapper,
            classNames?.wrapper,
          )}
        >
          <div
            className={cn(
              'absolute inset-y-0 left-0 flex h-full items-center pl-6',
              className?.before,
              classNames?.before,
            )}
          >
            {before}
          </div>
          <input
            type={type}
            className={cn(
              'h-full w-full flex-1 overflow-hidden rounded-[8px] bg-[#F9FAFB] px-6 text-sm hover:outline-none focus:outline-none lg:text-base',
              'file:h-full file:cursor-pointer file:rounded-md file:border-none file:border-input file:bg-secondary file:outline-none file:hover:bg-secondary/90',
              !!before && 'pl-14',
              !!after && 'pr-14',
              'ea-input__main',
              className?.main,
              classNames?.main,
            )}
            ref={ref}
            {...props}
          />
          <div
            className={cn(
              'absolute inset-y-0 right-0 flex h-full items-center pr-4',
              className?.after,
              classNames?.after,
            )}
          >
            {after}
          </div>
        </div>
        {afterOuter}
      </>
    );
  },
);
Input.displayName = 'Input';

export { Input };
